// ============================================
// Event Settings
// ============================================

import React from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Event } from '../../../types';

interface Props {
  event: Event;
}

export default function EventSettings({ event }: Props) {
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  const [formData, setFormData] = React.useState({
    name: event.name,
    venueName: event.venueName,
    startDate: event.startDate || '',
    endDate: event.endDate || '',
    city: event.city || '',
    country: event.country || '',
    tablesCount: event.tablesCount,
    seatsPerTable: event.seatsPerTable,
    assignmentMode: event.assignmentMode,
    pricePerMinute: event.pricePerMinute,
    currency: event.currency,
    therapistBillPct: event.therapistBillPct,
    therapistTipPct: event.therapistTipPct,
    acceptWindowMin: event.acceptWindowMin,
    orangeThresholdMin: event.orangeThresholdMin,
    allowPlayerLocationChanges: event.allowPlayerLocationChanges,
    locationChangeMaxCount: event.locationChangeMaxCount,
    locationChangeCooldownSec: event.locationChangeCooldownSec,
    bookingMinuteOptions: event.bookingMinuteOptions.join(', '),
    extendMinuteOptions: event.extendMinuteOptions.join(', '),
    defaultBookingMinutes: event.defaultBookingMinutes || 30,
    askCustomerAmount: event.askCustomerAmount ?? false,
    askCustomerRating: event.askCustomerRating ?? false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) : value,
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {
        ...formData,
        eventBillPct: 1 - formData.therapistBillPct,
        bookingMinuteOptions: formData.bookingMinuteOptions.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n)),
        extendMinuteOptions: formData.extendMinuteOptions.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n)),
      };
      await updateDoc(doc(db, 'events', event.id), updates);
      setSaved(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="event-settings">
      <div className="section-header">
        <h2>Event Settings</h2>
        {saved && <span className="saved-indicator">✓ Saved</span>}
      </div>

      <form className="settings-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        {/* Basic Info */}
        <section className="form-section">
          <h3>Basic Info</h3>
          <div className="form-group">
            <label>Event Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Venue Name</label>
            <input type="text" name="venueName" value={formData.venueName} onChange={handleChange} />
          </div>
        </section>

        {/* Date & Location */}
        <section className="form-section">
          <h3>Date & Location</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g., Barcelona" />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input type="text" name="country" value={formData.country} onChange={handleChange} placeholder="e.g., Spain" />
            </div>
          </div>
        </section>

        {/* Layout */}
        <section className="form-section">
          <h3>Layout</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Tables</label>
              <input type="number" name="tablesCount" value={formData.tablesCount} onChange={handleChange} min="1" />
            </div>
            <div className="form-group">
              <label>Seats per Table</label>
              <input type="number" name="seatsPerTable" value={formData.seatsPerTable} onChange={handleChange} min="2" max="12" />
            </div>
          </div>
        </section>

        {/* Assignment */}
        <section className="form-section">
          <h3>Assignment Mode</h3>
          <div className="form-group">
            <select name="assignmentMode" value={formData.assignmentMode} onChange={handleChange}>
              <option value="open_queue">Open Queue</option>
              <option value="auto_assign">Auto Assign</option>
            </select>
          </div>
        </section>

        {/* Pricing */}
        <section className="form-section">
          <h3>Pricing</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Currency</label>
              <select name="currency" value={formData.currency} onChange={handleChange}>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="NOK">NOK</option>
              </select>
            </div>
            <div className="form-group">
              <label>Price per Minute</label>
              <input type="number" name="pricePerMinute" value={formData.pricePerMinute} onChange={handleChange} step="0.5" min="0.5" />
            </div>
          </div>
        </section>

        {/* Splits */}
        <section className="form-section">
          <h3>Revenue Split</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Therapist Bill Share</label>
              <select name="therapistBillPct" value={formData.therapistBillPct} onChange={handleChange}>
                <option value="0.4">40%</option>
                <option value="0.5">50%</option>
                <option value="0.6">60%</option>
                <option value="0.7">70%</option>
              </select>
            </div>
            <div className="form-group">
              <label>Therapist Tip Share</label>
              <select name="therapistTipPct" value={formData.therapistTipPct} onChange={handleChange}>
                <option value="1.0">100%</option>
                <option value="0.9">90%</option>
                <option value="0.8">80%</option>
              </select>
            </div>
          </div>
        </section>

        {/* Timing */}
        <section className="form-section">
          <h3>Timing</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Accept Window (minutes)</label>
              <input type="number" name="acceptWindowMin" value={formData.acceptWindowMin} onChange={handleChange} min="1" max="30" />
            </div>
            <div className="form-group">
              <label>Orange Threshold (minutes)</label>
              <input type="number" name="orangeThresholdMin" value={formData.orangeThresholdMin} onChange={handleChange} min="5" max="30" />
            </div>
          </div>
        </section>

        {/* Player Location */}
        <section className="form-section">
          <h3>Player Location Changes</h3>
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="allowPlayerLocationChanges"
                checked={formData.allowPlayerLocationChanges}
                onChange={handleChange}
              />
              Allow players to change table/seat
            </label>
          </div>
          {formData.allowPlayerLocationChanges && (
            <div className="form-row">
              <div className="form-group">
                <label>Max Changes</label>
                <input type="number" name="locationChangeMaxCount" value={formData.locationChangeMaxCount} onChange={handleChange} min="1" max="10" />
              </div>
              <div className="form-group">
                <label>Cooldown (seconds)</label>
                <input type="number" name="locationChangeCooldownSec" value={formData.locationChangeCooldownSec} onChange={handleChange} min="0" max="300" />
              </div>
            </div>
          )}
        </section>

        {/* Booking Options */}
        <section className="form-section">
          <h3>Booking Options</h3>
          <div className="form-group">
            <label>Booking Minutes (comma-separated)</label>
            <input type="text" name="bookingMinuteOptions" value={formData.bookingMinuteOptions} onChange={handleChange} placeholder="10, 15, 20, 30" />
          </div>
          <div className="form-group">
            <label>Extend Minutes (comma-separated)</label>
            <input type="text" name="extendMinuteOptions" value={formData.extendMinuteOptions} onChange={handleChange} placeholder="5, 10, 15" />
          </div>
          <div className="form-group">
            <label>Default Duration (minutes)</label>
            <input type="number" name="defaultBookingMinutes" value={formData.defaultBookingMinutes} onChange={handleChange} min="5" step="5" />
            <small style={{ color: 'var(--color-text-secondary)', display: 'block', marginTop: '0.25rem' }}>
              Pre-selected duration when players open the booking page
            </small>
          </div>
        </section>

        {/* Customer Feedback */}
        <section className="form-section">
          <h3>Customer Feedback</h3>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
            After a session completes, ask the customer for feedback before they can book again.
          </p>
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="askCustomerAmount"
                checked={formData.askCustomerAmount}
                onChange={handleChange}
              />
              Ask customer how much they paid
            </label>
            <small style={{ color: 'var(--color-text-secondary)', display: 'block', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
              Helps verify therapist-reported amounts and detect discrepancies
            </small>
          </div>
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                name="askCustomerRating"
                checked={formData.askCustomerRating}
                onChange={handleChange}
              />
              Ask customer to rate the session (1-5 stars)
            </label>
            <small style={{ color: 'var(--color-text-secondary)', display: 'block', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
              Ratings visible only to admins in reports, not shown to therapists
            </small>
          </div>
        </section>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
